// CTV-682: the public storefront address printed on marketing materials.
//
// Deliberately NOT derived from window.location.origin. These sheets get
// printed and handed to advertisers, so a dev or preview host rendered onto
// one is a permanent mistake on paper — the URL must read the same wherever
// the page is previewed from.

/** Canonical public host for advertiser storefronts. */
export const STOREFRONT_HOST = 'streamline.ctvbuyer.com';

/**
 * Display form for print: no scheme, no trailing slash.
 *
 * `refCode` stamps the printing rep's referral code onto the URL so every
 * sheet they hand out attributes back to them. Omitted/blank leaves a clean
 * URL rather than an empty `?ref=`.
 */
export function storefrontUrl(
  slug: string,
  refCode?: string | null,
  promoCode?: string | null,
): string {
  const clean = slug.replace(/^\/+/, '').replace(/\/+$/, '');
  const base = clean ? `${STOREFRONT_HOST}/${clean}` : STOREFRONT_HOST;
  const params: string[] = [];
  const ref = refCode?.trim();
  if (ref) params.push(`ref=${ref}`);
  // CTV-840: a display ad run for a promotion carries the code, so a buyer
  // arriving from it has the promo applied (the storefront reads ?promo=,
  // the same parameter the Promotions tab's share link uses). After the ref
  // code, which is the part that must survive a truncated URL.
  const promo = promoCode?.trim();
  if (promo) params.push(`promo=${encodeURIComponent(promo)}`);
  return params.length ? `${base}?${params.join('&')}` : base;
}


/**
 * Campaign tagging for links that leave a printed sheet.
 *
 * Mirrors the convention our stations already use when they link in from
 * their own sites:
 *   ?utm_source=kitv&utm_medium=referral&utm_campaign=streamline&utm_content=ads_page
 *
 * with `utm_medium=print` instead of `referral`, so paper is distinguishable
 * from web. PostHog and GA4 both capture utm_* automatically, so nothing in
 * the app has to read these.
 *
 * Only the QR and the clickable link are tagged. A URL someone TYPES off the
 * page stays clean — partly because nobody types a query string correctly,
 * and partly because an untagged arrival is then a meaningful signal in
 * itself: it means they read the paper.
 */
export interface StorefrontUtm {
  /** Which artefact and entry point, e.g. `advertiser_onepager_qr`. */
  content: string;
  /**
   * CTV-840: display ads run on the station's site, not on paper, so they tag
   * `display` and stay distinguishable from print in the same reports.
   * CTV-856: the email signature graphic tags `email`.
   */
  medium?: 'print' | 'display' | 'email';
  /** CTV-840: a promotion code the arrival should apply. */
  promo?: string | null;
}

const UTM_CAMPAIGN = 'streamline';
const UTM_MEDIUM = 'print';

/**
 * Fully-qualified form for QR codes and PDF links. Scanners need the scheme:
 * given a bare `streamline.ctvbuyer.com/kitv` many treat the payload as plain
 * text and offer a web search instead of opening the link.
 *
 * `utm` tags the arrival. The referral code stays first so it survives a
 * truncated or hand-copied URL, which matters more than the analytics do.
 */
export function storefrontHref(
  slug: string,
  refCode?: string | null,
  utm?: StorefrontUtm,
): string {
  const base = `https://${storefrontUrl(slug, refCode, utm?.promo)}`;
  if (!utm) return base;
  const params = new URLSearchParams({
    utm_source: slug,
    utm_medium: utm.medium ?? UTM_MEDIUM,
    utm_campaign: UTM_CAMPAIGN,
    utm_content: utm.content,
  });
  return `${base}${base.includes('?') ? '&' : '?'}${params.toString()}`;
}
